import { plural, t } from '@lingui/core/macro';
import { Currency, QuoteStatus, TireQuoteRecordCustomer, TireQuoteRecordList } from '@navio/nsdk';
import { formatCurrencyAmount } from 'noddi-util';

import { getQuoteStatusValue } from './statusMapping';
import { TabKey } from './tabKeys';

type StatTone = 'brand' | 'warn' | 'default';

type TireOfferStat = {
  label: string;
  value: string | number;
  sub?: string;
  tone?: StatTone;
};

type Args = {
  activeTab: TabKey;
  acceptedOffers: TireQuoteRecordList[];
  pendingOffers: TireQuoteRecordCustomer[];
  pendingCount: number;
  pendingValue: number;
  selectedCount: number;
  currency: Currency | null | undefined;
};

export function getTireOfferStats({
  activeTab,
  acceptedOffers,
  pendingOffers,
  pendingCount,
  pendingValue,
  selectedCount,
  currency
}: Args): TireOfferStat[] {
  if (activeTab === 'accepted') {
    return [
      { label: t`Total accepted`, value: acceptedOffers.length, sub: t`in progress or done`, tone: 'brand' },
      {
        label: t`In transit`,
        value: countOffersByStatus(acceptedOffers, isInTransit),
        sub: t`from supplier`
      },
      {
        label: t`Mounting`,
        value: countOffersByStatus(acceptedOffers, isMounting),
        sub: t`at our facility`
      },
      {
        label: t`Done`,
        value: countOffersByStatus(acceptedOffers, isDone),
        sub: t`mounted & delivered`,
        tone: 'brand'
      }
    ];
  }

  return [
    {
      label: t`Pending offers`,
      value: pendingCount,
      sub: t`awaiting your decision`,
      tone: 'brand'
    },
    {
      label: t`Total value`,
      value: formatCurrencyAmount({
        money: { amount: pendingValue, currency: currency ?? undefined },
        decimals: 0
      }),
      sub: plural(selectedCount, { one: 'across # selected', other: 'across # selected' })
    },
    {
      label: t`Recommended`,
      value: pendingOffers.filter((offer) => offer.quoteItems.some((item) => item.isRecommended)).length,
      sub: t`from your account manager`
    },
    {
      label: t`Selected`,
      value: selectedCount,
      sub: plural(selectedCount, { one: 'offer', other: 'offers' })
    }
  ];
}

function countOffersByStatus(
  offers: TireQuoteRecordList[],
  predicate: (status: QuoteStatus | undefined) => boolean
): number {
  return offers.filter((offer) => predicate(getQuoteStatusValue(offer))).length;
}

function isInTransit(status: QuoteStatus | undefined): boolean {
  return status === QuoteStatus.INVENTORY_RECEIVED_IN_FULL || status === QuoteStatus.INVENTORY_RECEIVED_PARTIAL;
}

function isMounting(status: QuoteStatus | undefined): boolean {
  return (
    status === QuoteStatus.TIRE_MOUNTING_BOOKED ||
    status === QuoteStatus.TIRES_RECEIVED_AWAITING_MOUNTING_ON_RIM_AT_STORAGE_FACILITY ||
    status === QuoteStatus.TIRES_MOUNTED_ON_WHEELS_AT_STORAGE_FACILITY
  );
}

function isDone(status: QuoteStatus | undefined): boolean {
  return (
    status === QuoteStatus.TIRES_MOUNTED_ON_WHEELS_AT_CUSTOMER ||
    status === QuoteStatus.FULFILLED ||
    status === QuoteStatus.FULFILLED_PARTIALLY
  );
}
